// https://stackoverflow.com/questions/2901102/how-to-format-a-number-with-commas-as-thousands-separators

import moment from "moment";

export function moneyWithCommas(x: number) {
  return Math.floor(x) === x
    ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    : x.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function roundToDecimalDigits(value: number, digits = 5): number {
  return Math.round(value * (10 * digits)) / (10 * digits);
} // format the status of a hotel candidate to be more readable

export function formatDate(dateString: string) {
  return moment(dateString).format("MMMM D, YYYY");
}

export function formatDateNoYear(date: Date) {
  return moment(date).format("MMMM D");
}

export function formatInputDate(dateString: string) {
  return moment(dateString).format("YYYY-MM-DD");
}

export const formatTimeSince = (timestamp: string | Date) => {
  if (!timestamp) return "No contact";

  const lastUpdate = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();
  const diffSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

  if (diffSeconds < 60) {
    return "just now";
  }

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.ceil((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
  return `${diffDays}d ago`;
};

interface Parsed12HourTime {
  /** 1-12 */
  hour: number;
  /** "MM" (preserved with leading zero) */
  minutes: string;
  period: "AM" | "PM";
}

/**
 * Parse a 24-hour HH:MM[:SS] string into 12-hour clock parts.
 * @param timeString - Time string in HH:MM or HH:MM:SS format (24-hour)
 * @returns Parsed parts, or null if the string can't be parsed
 */
function parse12HourTime(timeString: string): Parsed12HourTime | null {
  const [hoursStr, minutesStr] = timeString.split(":");
  const hour = Number(hoursStr);
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return null;
  const period: "AM" | "PM" = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return { hour: displayHour, minutes: minutesStr ?? "00", period };
}

/** Render parsed parts. Optionally drop `:00` minutes and/or suppress the AM/PM suffix. */
function renderParsedTime(parts: Parsed12HourTime, includePeriod: boolean, dropZeroMinutes: boolean): string {
  const minutesPart = dropZeroMinutes && parts.minutes === "00" ? "" : `:${parts.minutes}`;
  return includePeriod ? `${parts.hour}${minutesPart} ${parts.period}` : `${parts.hour}${minutesPart}`;
}

/**
 * Format time from 24-hour HH:MM format to 12-hour AM/PM format.
 * Always includes minutes (e.g. "13:00" → "1:00 PM").
 * @param timeString - Time string in HH:MM format (24-hour)
 * @returns Formatted time string in 12-hour format with AM/PM
 */
export function formatTime(timeString: string): string {
  const parsed = parse12HourTime(timeString);
  if (!parsed) return timeString;
  return renderParsedTime(parsed, true, false);
}

/**
 * Format a start/end time pair as a display range.
 * - Drops `:00` minutes for brevity (e.g. "13:00" → "1", "13:30" → "1:30").
 * - Collapses shared AM/PM when both times are in the same period
 *   (e.g. "08:00" + "09:00" → "8 - 9 AM").
 * Falls back to a single formatted time if only one side is provided.
 *
 * @param startTime - Start time in HH:MM (24-hour), or null/undefined
 * @param endTime - End time in HH:MM (24-hour), or null/undefined
 * @param separator - Joiner between start and end (default " - ")
 * @returns Formatted range, or "" if neither time is provided
 */
export function formatTimeRange(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  separator: string = " - "
): string {
  if (startTime && endTime) {
    const start = parse12HourTime(startTime);
    const end = parse12HourTime(endTime);
    if (!start || !end) return `${formatTime(startTime)}${separator}${formatTime(endTime)}`;
    const samePeriod = start.period === end.period;
    return `${renderParsedTime(start, !samePeriod, true)}${separator}${renderParsedTime(end, true, true)}`;
  }
  if (startTime) return formatTime(startTime);
  if (endTime) return formatTime(endTime);
  return "";
}

/**
 * Format a date string as short month and day
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string (e.g., "Jan 5, 2025")
 */
export function formatDateShort(dateString: string): string {
  try {
    return moment(dateString, "YYYY-MM-DD").format("MMM D, YYYY");
  } catch {
    return dateString;
  }
}

/**
 * Format a proposed start→end date range compactly, collapsing shared parts:
 * same month & year → "Sep 14 – 17, 2026"; same year → "Sep 28 – Oct 1, 2026";
 * different years → "Dec 30, 2026 – Jan 2, 2027". Falls back to a single
 * {@link formatDateShort} when only one endpoint is present.
 * @param start - Start date (YYYY-MM-DD) or null.
 * @param end - End date (YYYY-MM-DD) or null.
 * @returns The formatted range, or null when both endpoints are missing.
 */
export function formatDateRangeShort(start: string | null, end: string | null): string | null {
  if (!start && !end) return null;
  if (!start) return formatDateShort(end as string);
  if (!end) return formatDateShort(start);

  const s = moment(start, "YYYY-MM-DD");
  const e = moment(end, "YYYY-MM-DD");
  if (!s.isValid() || !e.isValid()) return formatDateShort(start);

  if (s.year() !== e.year()) return `${s.format("MMM D, YYYY")} – ${e.format("MMM D, YYYY")}`;
  if (s.month() !== e.month()) return `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
  return `${s.format("MMM D")} – ${e.format("D, YYYY")}`;
}

/**
 * Format a date string as "Wed, Oct 16" — short weekday + short month + day, no year. Used in
 * per-date table rows (guestroom nights, meeting-room days) where every row shares the event's
 * current year, so showing the actual calendar date — not just an ordinal position — is what
 * clears up which row corresponds to which real day.
 * @param dateString - Date string in YYYY-MM-DD format.
 * @returns Formatted date string, or the raw input if unparseable.
 */
export function formatDateWithWeekday(dateString: string): string {
  try {
    return moment(dateString, "YYYY-MM-DD").format("ddd, MMM D");
  } catch {
    return dateString;
  }
}

/**
 * Get the day of week from a date string
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Day of week (e.g., "Monday")
 */
export function formatDayOfWeek(dateString: string): string {
  try {
    return moment(dateString, "YYYY-MM-DD").format("dddd");
  } catch {
    return "";
  }
}

/**
 * Short three-letter weekday for a date.
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Short weekday (e.g., "Mon")
 */
export function formatDayOfWeekShort(dateString: string): string {
  try {
    return moment(dateString, "YYYY-MM-DD").format("ddd");
  } catch {
    return "";
  }
}

/**
 * Day-of-month number with no leading zero.
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Day number (e.g., "8")
 */
export function formatDayNumber(dateString: string): string {
  try {
    return moment(dateString, "YYYY-MM-DD").format("D");
  } catch {
    return "";
  }
}

/**
 * Full month name for a date.
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Month name (e.g., "August")
 */
export function formatMonthLong(dateString: string): string {
  try {
    return moment(dateString, "YYYY-MM-DD").format("MMMM");
  } catch {
    return "";
  }
}

/**
 * Returns the effective time for sorting: prefers start_time, falls back to end_time
 * @param startTime - Start time in HH:MM format (24-hour), or null
 * @param endTime - End time in HH:MM format (24-hour), or null
 * @returns The effective time string for sorting, or empty string if both are null
 */
export function getEffectiveTime(startTime: string | null, endTime: string | null): string {
  return startTime ?? endTime ?? "";
}

/** Shape required for schedule item time-based sorting */
interface ScheduleTimeSortable {
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  sort_order: number;
  created_at: string;
}

/**
 * Comparator for sorting schedule items by date then effective time (start_time ?? end_time)
 * @param a - First schedule item
 * @param b - Second schedule item
 * @returns Negative if a comes first, positive if b comes first, 0 if equal
 */
export function compareScheduleItemsByTime(a: ScheduleTimeSortable, b: ScheduleTimeSortable): number {
  const dateA = a.date ?? "zzzz";
  const dateB = b.date ?? "zzzz";
  if (dateA !== dateB) return dateA.localeCompare(dateB);
  const timeA = getEffectiveTime(a.start_time, a.end_time);
  const timeB = getEffectiveTime(b.start_time, b.end_time);
  if (timeA !== timeB) return timeA.localeCompare(timeB);
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.created_at.localeCompare(b.created_at);
}
